import { useCallback, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { ZakresTrafienia } from "@shared/powtarzaneSlowa.js";
import { sciezkaLinku, type RecenzjaRozmowa } from "@shared/recenzjeTypy.js";
import { IconChevronLewo } from "../../components/icons/index.js";
import { AkcjeKoncowe } from "../../components/recenzja/AkcjeKoncowe.js";
import { KartaNotatki } from "../../components/recenzja/KartaNotatki.js";
import { KartaPowtarzanychSlow } from "../../components/recenzja/KartaPowtarzanychSlow.js";
import { KartaUwagDoRozmowy } from "../../components/recenzja/KartaUwagDoRozmowy.js";
import { NiewaznyLink } from "../../components/recenzja/NiewaznyLink.js";
import { PasekPostepu } from "../../components/recenzja/PasekPostepu.js";
import { StatusZapisu } from "../../components/recenzja/StatusZapisu.js";
import { TranskryptRecenzji } from "../../components/recenzja/TranskryptRecenzji.js";
import { WskazanieRecenzji } from "../../components/recenzja/WskazanieRecenzji.js";
import { Banner } from "../../components/ui/Banner.js";
import { Button } from "../../components/ui/Button.js";
import { Card } from "../../components/ui/Card.js";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog.js";
import { StanBledu, StanLadowania, StanPusty } from "../../components/ui/stany.js";
import { DostawcaPrzegladu, DostawcaStatusuZapisu, usePrzegladRecenzji } from "../../hooks/usePrzegladRecenzji.js";
import { tytulRozmowyRecenzji, useTytulStrony } from "../../hooks/useTytulStrony.js";
import { useZasob } from "../../hooks/useZasob.js";
import { powtarzaneSlowaRecenzji } from "../../lib/powtarzaneSlowa.js";
import {
  TEKST_OCENA_PRZELICZANA,
  napisPostepuRozmowy,
  rodzajBledu,
  tekstBledu,
  wskazaniaWgWypowiedzi,
} from "../../lib/recenzja.js";
import { recenzjaApi } from "../../lib/recenzjaApi.js";
import { tytulUsunieciaUwagi } from "../../lib/uwagiWypowiedzi.js";

/**
 * Przeglad jednej rozmowy: pasek postepu z autozapisem i licznikiem uwag, transkrypt ze wskazaniami modelu, werdyktami
 * i uwagami recenzenta przy wypowiedziach, "Powtarzane slowa" (liczenie slow z listy metodyki, tylko do odczytu, przyciski
 * "Wypowiedz N" przewijaja transkrypt), notatka know-how i zakonczenie. Dane wczytujemy RAZ: szkice pol zyja
 * w komponentach, wiec zadne odswiezanie z serwera nie moze ich nadpisac.
 */

function PrzegladRozmowy({ token, rozmowa }: { token: string; rozmowa: RecenzjaRozmowa }): React.JSX.Element {
  const { postep, migawka, ponow, status, akcje, uwagi } = usePrzegladRecenzji(
    token,
    rozmowa.id,
    rozmowa.wypowiedzi.length,
    rozmowa.postep,
    rozmowa.wskazania,
    rozmowa.pominiete,
    rozmowa.parametry,
  );
  const { cel: celUsuniecia, potwierdz, anuluj } = uwagi.usuwanie;
  const { bezWypowiedzi } = useMemo(() => wskazaniaWgWypowiedzi(rozmowa), [rozmowa]);
  // Liczenie slow z listy metodyki na wypowiedziach, ktore recenzent juz ma: bez modelu i bez zapytania do serwera.
  const powtarzane = useMemo(() => powtarzaneSlowaRecenzji(rozmowa.wypowiedzi), [rozmowa]);
  /** Prosba z karty "Powtarzane slowa" o pokazanie wypowiedzi i zaznaczenie w niej slowa; `token` rosnie przy kazdej prosbie. */
  const [skokDo, setSkokDo] = useState<{ numer: number; token: number; trafienia?: readonly ZakresTrafienia[] } | null>(
    null,
  );
  const pokazWypowiedz = useCallback(
    (numer: number, trafienia: readonly ZakresTrafienia[]) =>
      setSkokDo((poprzedni) => ({ numer, token: (poprzedni?.token ?? 0) + 1, trafienia })),
    [],
  );
  const powrot = sciezkaLinku(token);
  useTytulStrony(tytulRozmowyRecenzji(rozmowa.tytul));

  // Trzy bledy zapisu zmieniaja sytuacje (link i ocena na stale, przeliczanie oceny na chwile), wiec dostaja baner
  // (BN-3: jeden naraz). Reszta (siec, serwer) to chwilowa awaria: wystarczy status przy pasku z przyciskiem ponowienia.
  // Przy przeliczaniu oceny serwer odpowiada 409 na kazdy zapis, a szkice zostaja w polach i w magazynie, wiec baner
  // niczego nie kasuje: "Sprobuj ponownie" (w pasku postepu) ponawia zapis, gdy ocena bedzie gotowa.
  // Ostrzezenia o ocenie ida banerem informacji, nie ostrzezenia: w tym widoku bursztyn znaczy "najlepsze" (zloto oceny,
  // decyzja autora metodyki z 29.10.2026). Sens niesie tekst i ikona banera (ST-3). Oba mowia o zapisie, ktory nie
  // przeszedl, wiec sa pilne: czytnik oglasza je od razu (role="alert"), tak jak wczesniej ostrzezenie.
  const rodzaj = migawka.stan === "error" ? rodzajBledu(migawka.blad) : null;
  const baner =
    rodzaj === "link" ? (
      <Banner wariant="danger" tekst={tekstBledu(migawka.blad)} />
    ) : rodzaj === "nieoceniona" ? (
      <Banner wariant="info" pilny tekst={TEKST_OCENA_PRZELICZANA} />
    ) : rodzaj === "ocena" ? (
      <Banner
        wariant="info"
        pilny
        tekst={tekstBledu(migawka.blad)}
        akcja={
          <Button wariant="secondary" onClick={() => window.location.reload()}>
            Wczytaj ponownie
          </Button>
        }
      />
    ) : null;

  return (
    <DostawcaPrzegladu value={akcje}>
      <DostawcaStatusuZapisu value={status}>
        <Link className="rec-powrot" to={powrot}>
          <IconChevronLewo size={16} />
          Wszystkie rozmowy
        </Link>
        <h1 className="page-title">{rozmowa.tytul}</h1>
        <p className="page-lead">
          Przejrzyj wskazania modelu. Tam, gdzie się zgadzasz, nic nie musisz klikać. Do dowolnej wypowiedzi możesz dopisać
          własną uwagę.
        </p>

        {baner}

        <div className="rec-sticky">
          <Card>
            <div className="rec-postep">
              <span className="rec-postep__napis">{napisPostepuRozmowy(postep)}</span>
              <PasekPostepu postep={postep} />
              {/* Liczy uwagi ZAPISANE na serwerze (wspolna lista), nie to, co recenzent dopiero pisze. */}
              <span className="rec-postep__uwagi">
                Twoich uwag: <strong>{uwagi.liczba}</strong>
              </span>
              <StatusZapisu migawka={migawka} onPonow={ponow} />
            </div>
          </Card>
        </div>

        <Card tytul="Rozmowa">
          {rozmowa.opis && <p className="opis-rozmowy">{rozmowa.opis}</p>}
          <TranskryptRecenzji rozmowa={rozmowa} widok={uwagi.widok} skokDo={skokDo} />
        </Card>

        {bezWypowiedzi.length > 0 && (
          <Card tytul="Wskazania bez przypisanej wypowiedzi">
            <p className="page-lead">
              Model nie wskazał przy nich konkretnej wypowiedzi, więc nie ma ich w transkrypcie. Oceń je tutaj.
            </p>
            <div className="rec-bez-wypowiedzi">
              {bezWypowiedzi.map((w) => (
                <WskazanieRecenzji key={w.klucz} wskazanie={w} />
              ))}
            </div>
          </Card>
        )}

        {/* Tylko do odczytu: liczenie slow, nie ocena, wiec bez werdyktow i bez zapisu. Stoi przed notatka, bo to z niej
            recenzent moze wyciagnac hipotezy do zapisania. */}
        <KartaPowtarzanychSlow mowcy={rozmowa.mowcy} powtarzane={powtarzane} onPokaz={pokazWypowiedz} />
        <KartaUwagDoRozmowy wpisy={uwagi.bezWypowiedzi} />
        <KartaNotatki poczatkowa={rozmowa.notatka} />
        <AkcjeKoncowe przejrzana={postep.przejrzana} powrot={powrot} />

        <ConfirmDialog
          otwarty={celUsuniecia !== null}
          tytul={celUsuniecia ? tytulUsunieciaUwagi(celUsuniecia.numerWypowiedzi) : "Usunąć uwagę?"}
          tresc="Tej operacji nie można cofnąć."
          etykietaPotwierdzenia="Usuń uwagę"
          onPotwierdz={potwierdz}
          onAnuluj={anuluj}
        />
      </DostawcaStatusuZapisu>
    </DostawcaPrzegladu>
  );
}

/** Strona /r/:token/rozmowa/:sessionId z czterema stanami (P-6): ladowanie, blad, brak oceny i dane. */
export function RozmowaRecenzjiPage(): React.JSX.Element {
  const { token = "", sessionId = "" } = useParams();
  const klucz = `rozmowa:${token}:${sessionId}`;
  const { dane, kluczDanych, blad, odswiez } = useZasob<RecenzjaRozmowa>(klucz, () =>
    recenzjaApi.rozmowa(token, sessionId),
  );
  const powrot = sciezkaLinku(token);

  if (dane !== null && kluczDanych === klucz) {
    // key: przejscie na inna rozmowe zaczyna od czystego stanu (szkice, postep, kolejka zapisu).
    return <PrzegladRozmowy key={dane.id} token={token} rozmowa={dane} />;
  }

  if (blad === "link_nieprawidlowy") return <NiewaznyLink />;

  const wrocDoListy = (
    <Link className="btn btn--secondary" to={powrot}>
      Wszystkie rozmowy
    </Link>
  );

  return (
    <>
      <Link className="rec-powrot" to={powrot}>
        <IconChevronLewo size={16} />
        Wszystkie rozmowy
      </Link>
      <h1 className="page-title">Rozmowa</h1>
      <Card>
        {blad === null && <StanLadowania tekst="Wczytuję rozmowę..." />}

        {blad === "nie_ma_takiej_rozmowy" && (
          <StanPusty
            tytul="Tej rozmowy nie ma pod tym linkiem"
            opis="Mogła zostać usunięta albo link jej nie obejmuje. Wróć do listy rozmów."
            akcja={wrocDoListy}
          />
        )}

        {blad === "rozmowa_nieoceniona" && (
          <StanPusty
            tytul="Ta rozmowa nie ma jeszcze gotowej oceny"
            opis="Wróć za jakiś czas albo wybierz inną rozmowę z listy."
            akcja={wrocDoListy}
          />
        )}

        {blad !== null && blad !== "nie_ma_takiej_rozmowy" && blad !== "rozmowa_nieoceniona" && (
          <StanBledu
            szczegol={blad}
            akcja={
              <Button wariant="secondary" onClick={odswiez}>
                Spróbuj ponownie
              </Button>
            }
          />
        )}
      </Card>
    </>
  );
}
